import { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { formatCurrency } from '@/lib/format.js';
import { ApiError } from '@/lib/api.js';
import { getSubmissionRecord, fetchQuoteDoc } from '@/services/quotes.service.js';
import { formatDateTime, formatDateOrEmpty } from '@/lib/date-display.js';

// Submission record (SUBMISSION-RECORD-SPEC): what we submitted, what we
// received, and the documents it generated, for one captured quote.
//
// Broker-only. The Submitted payload carries pilot names, dates of birth,
// medical and claims history, so this drawer and its endpoints are never
// reachable from the partner portal.
//
// Renders what was sent and received. It derives nothing: no recomputed
// premiums, no inferred status, no reconstructed request. A field we do not
// have reads as not captured rather than as a guess.

const TABS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'received', label: 'Received' },
  { key: 'docs', label: 'Docs' },
];

// Keys worth leading with when we know what they mean. Everything else still
// renders, just after these, so an unfamiliar carrier field is never hidden.
const GROUP_ORDER = ['insureds', 'aircraftUnits', 'pilots', 'coverageDate', 'datasource'];

const isObj = (v) => v !== null && typeof v === 'object';
const labelize = (k) =>
  String(k).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());

function Scalar({ value }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-text-dim">not set</span>;
  }
  if (typeof value === 'boolean') return <span className="text-text">{value ? 'yes' : 'no'}</span>;
  return <span className="text-text break-words">{String(value)}</span>;
}

// A readable key/value tree, not a raw dump. Arrays of objects become numbered
// blocks (aircraft 1, pilot 2) because that is how a broker reads a submission.
function Node({ k, value, depth = 0 }) {
  const pad = depth > 0 ? 'ml-3 border-l border-border/60 pl-3' : '';

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div className={`py-0.5 ${pad}`}>
          <span className="text-[11px] uppercase tracking-wide text-text-dim">{labelize(k)}</span>{' '}
          <span className="text-[12px] text-text-dim">none</span>
        </div>
      );
    }
    return (
      <div className={`py-1 ${pad}`}>
        <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
          {labelize(k)} <span className="text-text-dim">({value.length})</span>
        </p>
        {value.map((v, i) => (
          <div key={i} className="mt-1">
            {isObj(v)
              ? <Node k={`${labelize(k).replace(/s$/, '')} ${i + 1}`} value={v} depth={depth + 1} />
              : <div className="ml-3 text-[12px]"><Scalar value={v} /></div>}
          </div>
        ))}
      </div>
    );
  }

  if (isObj(value)) {
    const keys = Object.keys(value);
    const ordered = [
      ...GROUP_ORDER.filter((g) => keys.includes(g)),
      ...keys.filter((x) => !GROUP_ORDER.includes(x)),
    ];
    return (
      <div className={`py-1 ${pad}`}>
        <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">{labelize(k)}</p>
        {ordered.map((ck) => <Node key={ck} k={ck} value={value[ck]} depth={depth + 1} />)}
      </div>
    );
  }

  return (
    <div className={`flex gap-2 py-0.5 text-[12px] ${pad}`}>
      <span className="min-w-[9rem] shrink-0 text-text-muted">{labelize(k)}</span>
      <Scalar value={value} />
    </div>
  );
}

function Empty({ children }) {
  return <p className="px-1 py-6 text-center text-[12.5px] leading-relaxed text-text-muted">{children}</p>;
}

function RawToggle({ value }) {
  const [open, setOpen] = useState(false);
  if (value == null) return null;
  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] text-text-dim underline decoration-dotted underline-offset-2 hover:text-text-muted"
      >
        {open ? 'Hide raw' : 'Show raw'}
      </button>
      {open ? (
        <pre className="mt-2 max-h-80 overflow-auto rounded-[5px] border border-border bg-bg p-2 text-[10.5px] leading-snug text-text-muted">
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

export default function SubmissionRecordDrawer({ quoteId, open, onOpenChange }) {
  const [tab, setTab] = useState('submitted');
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getSubmissionRecord(quoteId);
      setRecord(res?.record || null);
    } catch (err) {
      setError(err instanceof ApiError ? err.body?.error || err.code : err.message);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => { if (open) { setTab('submitted'); load(); } }, [open, load]);

  // Fetch then open a blob URL. A direct link would open a tab with no
  // Authorization header and land on a 401 rather than the PDF.
  const [opening, setOpening] = useState(null);
  const openDoc = async (ref, name) => {
    setOpening(ref);
    try {
      const blob = await fetchQuoteDoc(quoteId, ref);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Give the new tab time to take the handle before releasing it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(`Could not open ${name}: ${err.message}`);
    } finally {
      setOpening(null);
    }
  };

  const submitted = record?.submitted;
  const received = record?.received;
  const docs = record?.docs || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Submission record</SheetTitle>
          <SheetDescription>
            {record
              ? [record.carrier_name, record.quote_number ? `quote ${record.quote_number}` : null]
                  .filter(Boolean).join(', ')
              : 'What we submitted, what came back, and the documents it generated.'}
          </SheetDescription>
        </SheetHeader>

        {record?.premium != null ? (
          <p className="text-[12px] text-text-muted">
            Premium {formatCurrency(record.premium)} <span className="text-text-dim">pre-tax</span>
          </p>
        ) : null}

        <div className="flex gap-1 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-[12.5px] transition ${
                tab === t.key
                  ? 'border-b-2 border-accent font-medium text-text'
                  : 'text-text-muted hover:text-text'
              }`}
            >
              {t.label}
              {t.key === 'docs' && docs.length ? (
                <span className="ml-1 text-[10px] text-text-dim">{docs.length}</span>
              ) : null}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="flex items-center gap-2 px-1 py-6 text-[12.5px] text-text-muted">
            <Loader2 size={14} className="animate-spin" /> Loading the record...
          </p>
        ) : error ? (
          <Empty>Could not load this record: {error}</Empty>
        ) : !record ? (
          <Empty>Nothing to show for this quote.</Empty>
        ) : (
          <>
            {tab === 'submitted' ? (
              submitted ? (
                <div>
                  <p className="mb-1 text-[11px] text-text-dim">
                    {submitted.sent_at
                      ? `Sent ${formatDateTime(submitted.sent_at)}`
                      : 'Send time was not captured on this quote.'}
                  </p>
                  {submitted.format === 'json' ? (
                    <div className="rounded-[5px] border border-border bg-surface px-2.5 py-2">
                      <Node k="request" value={submitted.payload} />
                    </div>
                  ) : (
                    <pre className="max-h-96 overflow-auto rounded-[5px] border border-border bg-bg p-2 text-[10.5px] text-text-muted">
                      {String(submitted.payload)}
                    </pre>
                  )}
                  <RawToggle value={submitted.payload} />
                </div>
              ) : record.not_implemented ? (
                <Empty>{record.not_implemented}</Empty>
              ) : (
                <Empty>This quote was entered manually, nothing was submitted through an API.</Empty>
              )
            ) : null}

            {tab === 'received' ? (
              received?.raw || received?.parsed ? (
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    {/* `outline`, NOT `muted`. Same defect as the coverage chip on
                        FleetPanel: `muted` is `bg-border/40 text-text-dim
                        line-through`, and a STRIKETHROUGH means the thing is
                        cancelled. A QUOTE STATUS IS NOT A CANCELLED THING:
                        `received`, `presented` and `selected` are all live
                        states, and every one of them has been rendering struck
                        through on the carrier response tab. The variant was
                        picked for the word "muted". */}
                    {received.status ? <Badge variant="outline">{received.status}</Badge> : null}
                    {received.received_at ? (
                      <span className="text-[11px] text-text-dim">
                        {formatDateTime(received.received_at)}
                      </span>
                    ) : null}
                  </div>
                  <div className="rounded-[5px] border border-border bg-surface px-2.5 py-2">
                    <Node k="response" value={received.parsed || received.raw} />
                  </div>
                  <RawToggle value={received.raw} />
                </div>
              ) : (
                <Empty>No carrier response is stored on this quote.</Empty>
              )
            ) : null}

            {tab === 'docs' ? (
              record.docs_error ? (
                <Empty>Could not reach the carrier for documents: {record.docs_error}</Empty>
              ) : docs.length ? (
                <ul className="divide-y divide-border">
                  {docs.map((d) => (
                    <li key={d.retrieve_ref} className="flex items-center gap-2 py-2">
                      <FileText size={14} strokeWidth={1.6} className="shrink-0 text-text-dim" />
                      <button
                        type="button"
                        onClick={() => openDoc(d.retrieve_ref, d.name)}
                        disabled={opening === d.retrieve_ref}
                        className="min-w-0 flex-1 text-left disabled:opacity-60"
                      >
                        <span className="block truncate text-[12.5px] text-text hover:underline">{d.name}</span>
                        {opening === d.retrieve_ref ? (
                          <span className="block text-[11px] text-text-dim">Opening...</span>
                        ) : d.created_at ? (
                          <span className="block text-[11px] text-text-dim">
                            {formatDateOrEmpty(d.created_at, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        ) : null}
                      </button>
                      {d.status ? <span className="shrink-0 text-[11px] text-text-dim">{d.status}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty>Global has generated no documents for this quote yet.</Empty>
              )
            ) : null}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
